import { Controller } from "@hotwired/stimulus"

/**
 * PdfViewerController - Maneja el visor de PDF en sidebar con navegación
 * Conecta a data-controller="pdf-viewer"
 */
export default class extends Controller {
  static targets = [
    "sidebar",
    "overlay",
    "loading",
    "iframe",
    "tasks",
    "zone",
    "allAccess",
    "passType",
    "printButton",
    "employee"
  ]
  static values = {
    selectedZones: { type: Array, default: [] }
  }

  connect() {
    this.selectedRow = null
    this.allRows = []
    this.currentIndex = -1
    this.boundHandleEscape = this.handleEscape.bind(this)
    this.boundHandleKeyNavigation = this.handleKeyNavigation.bind(this)
    // this.boundHandleScroll = this.handleScroll.bind(this)

    this.setupEventListeners()
  }

  disconnect() {
    this.removeEventListeners()
  }

  selectRow(event) {
    if (this.shouldIgnoreClick(event)) return

    const row = event.currentTarget
    const passId = row.dataset.passId

    if (!passId) return

    this.loadPassById(passId, row)
  }

  showTemplate() {
    // Limpiar selección actual si existe
    if (this.selectedRow) {
      this.selectedRow.classList.remove('table-active')
      this.selectedRow = null
      this.currentIndex = -1
    }

    // Mostrar el template
    this.showSidebar()
    this.renderTemplate()
  }

  renderTemplate() {
    const templateHtml = this.element.querySelector('#pdf-template-content').innerHTML
    const viewer = document.getElementById('pdf-viewer')
    if (viewer) {
      viewer.innerHTML = templateHtml
    }
  }

  navigatePrevious() {
    this.updateRowsList()

    if (this.currentIndex > 0) {
      const previousRow = this.allRows[this.currentIndex - 1]
      const passId = previousRow.dataset.passId

      if (passId) {
        this.loadPassById(passId, previousRow)
      }
    }
  }

  navigateNext() {
    this.updateRowsList()

    if (this.currentIndex < this.allRows.length - 1) {
      const nextRow = this.allRows[this.currentIndex + 1]
      const passId = nextRow.dataset.passId

      if (passId) {
        this.loadPassById(passId, nextRow)
      }
    }
  }

  hideLoadingState() {
    const loadingElements = [
      ...document.querySelectorAll('[data-pdf-viewer-target="loading"]'),
      ...document.querySelectorAll('.loading-overlay')
    ]

    loadingElements.forEach(element => {
      // element.style.display = 'none'
      element.classList.remove('d-flex')
      element.classList.add('d-none')
    })
  }

  hideLoading() {
    this.hideLoadingState()
  }

  showSidebar() {
    if (this.hasSidebarTarget) this.sidebarTarget.classList.add('show')
    if (this.hasOverlayTarget) this.overlayTarget.classList.add('show')
    document.body.style.overflow = 'hidden'
  }

  closeSidebar(event) {
    event?.preventDefault()

    if (this.hasSidebarTarget) this.sidebarTarget.classList.remove('show')
    if (this.hasOverlayTarget) this.overlayTarget.classList.remove('show')
    document.body.style.overflow = ''
    this.clearSelection()
  }

  showErrorState() {
    const errorHtml = this.getErrorHTML()

    Turbo.renderStreamMessage(`
      <turbo-stream action="replace" target="pdf-viewer">
        <template>${errorHtml}</template>
      </turbo-stream>
    `)
  }

  // Métodos de utilidad
  setupEventListeners() {
    document.addEventListener('keydown', this.boundHandleEscape)
    document.addEventListener('keydown', this.boundHandleKeyNavigation)
    // document.addEventListener('scroll', this.boundHandleScroll)
  }

  removeEventListeners() {
    document.removeEventListener('keydown', this.boundHandleEscape)
    document.removeEventListener('keydown', this.boundHandleKeyNavigation)
    // document.removeEventListener('scroll', this.boundHandleScroll)
  }

  shouldIgnoreClick(event) {
    const ignoredSelectors = ['.form-check', '.dropdown', 'button', 'a']
    return ignoredSelectors.some(selector => event.target.closest(selector))
  }

  loadPassById(passId, row = null) {
    this.updateRowsList()

    if (!row) row = document.querySelector(`tr[data-pass-id="${passId}"]`)
    else this.currentIndex = this.allRows.indexOf(row)

    this.updateActiveRow(row)
    this.showSidebar()
    this.fetchPDF(passId)
  }

  updateActiveRow(row) {
    if (this.selectedRow) {
      this.selectedRow.classList.remove('table-active')
    }

    if (row) {
      row.classList.add('table-active')
      this.selectedRow = row
    }
  }

  updateRowsList() {
    this.allRows = Array.from(document.querySelectorAll('tr[data-pass-id]'))
  }

  clearSelection() {
    if (this.selectedRow) {
      this.selectedRow.classList.remove('table-active')
    }
    this.selectedRow = null
    this.currentIndex = -1
  }

  handleKeyNavigation = (event) => {
    if (!this.isSidebarOpen()) return

    const keyActions = {
      'ArrowLeft': () => this.navigatePrevious(),
      'ArrowUp': () => this.navigatePrevious(),
      'ArrowRight': () => this.navigateNext(),
      'ArrowDown': () => this.navigateNext()
    }

    const action = keyActions[event.key]
    if (action) {
      event.preventDefault()
      action()
    }
  }

  handleEscape = (event) => {
    if (event.key === 'Escape') {
      this.closeSidebar()
    }
  }

  handleScroll = (event) => {
    const actions = document.querySelector('.floating-actions')
    if (!actions) return

    if (window.scrollY > 100) actions.classList.add('scrolled')
    else actions.classList.remove('scrolled')
  }


  isSidebarOpen() {
    return this.hasSidebarTarget && this.sidebarTarget.classList.contains('show')
  }

  getErrorHTML() {
    return `
      <div class="pdf-sidebar-content">
        <div class="text-center py-5">
          <i class="bi bi-exclamation-triangle display-4 mb-3 text-danger"></i>
          <h5 class="text-danger mb-3">Error al cargar el PDF</h5>
          <p class="text-muted mb-0">
            No se pudo cargar la acreditación seleccionada.<br>
            Por favor, inténtalo de nuevo.
          </p>
        </div>
      </div>
    `
  }

  updatePassType(event) {
    event.preventDefault()
    event.stopPropagation()

    const {passId} = event.target.dataset
    const {value} = event.target

    this.updatePass(passId, { pass_type_id: value })
  }

  async toggleAllAccess(event) {
    event.preventDefault()
    event.stopPropagation()

    const {passId} = event.target.dataset
    const allAccess = event.target.checked

    this.zoneTargets.forEach((zoneCheckbox) => {
      zoneCheckbox.disabled = allAccess
      if (allAccess) zoneCheckbox.checked = false
    })

    await this.updatePass(passId, { all_access: allAccess, zone_ids: [] })
  }

  async toggleEmployee(event) {
    event.preventDefault()
    event.stopPropagation()

    const {passId} = event.target.dataset
    const isEmployee = event.target.checked

    await this.updatePass(passId, { employee: isEmployee })
  }

  updateZones(event) {
    event.preventDefault()
    event.stopPropagation()

    const {passId} = event.target.dataset
    const selectedOptions = this.zoneTargets
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value)

    this.updatePass(passId, { zone_ids: selectedOptions })
  }

  async updateTasks(event) {
    event.preventDefault()
    event.stopPropagation()

    const {passId} = event.target.dataset
    const {value} = event.target

    event.target.value = value.toUpperCase()

    await this.updatePass(passId, { tasks: value.toUpperCase() })
  }

  async updatePass(passId, params) {
    try {
      const row = document.querySelector(`tr[data-pass-id="${passId}"]`)
      const index = Array.from(row.parentElement.children).indexOf(row)

      const response = await fetch(`/passes/${passId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html'
        },
        body: JSON.stringify({ pass: params, index: index })
      })

      if (response.ok) {
        const iframe = this.iframeTarget
        const html = await response.text()

        iframe.src = iframe.src.split('#')[0] + '#' + new Date().getTime()
        Turbo.renderStreamMessage(html)
      }
    } catch (error) {
      console.error('Error updating pass:', error)
    }
  }

  async fetchPDF(passId) {
    try {
      const response = await fetch(`/passes/${passId}`, {
        method: 'GET',
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const html = await response.text()
      Turbo.renderStreamMessage(html)
    } catch (e) {
      console.error('Error loading PDF preview:', error)
      this.showErrorState()
    }
  }

  print(event) {
    event.preventDefault()
    const iframe = this.iframeTarget
    if (iframe) {
      const currentUrl = new URL(iframe.src)
      const printFrame = document.createElement('iframe')

      printFrame.style.display = 'none'
      printFrame.src = currentUrl.pathname.replace('/preview', '/print')

      document.body.appendChild(printFrame)

      printFrame.onload = () => {
        printFrame.contentWindow.print()
        // Remover el iframe temporal después de imprimir
        // setTimeout(() => {
        //   document.body.removeChild(printFrame)
        // }, 1000)
      }
    }
  }
}
